import { pipe, ReadonlyArray } from "fp";

import React from "react";
import { CSVLink } from "react-csv";

import { Asset } from "~/Asset";
import * as Button from "~/Button";
import * as Driver from "~/Driver";
import * as ELD from "~/ELD";
import { NotApplicable } from "~/NotApplicable";
import { Time } from "~/Time";

type Download = {
  readonly "Report Start Date": string;
  readonly "Report Stop Date": string;
  readonly "Submission ID": string;
  readonly "Submission Date": string;
  readonly "Submitted By": string;
  readonly "Submission Status": string;
  readonly "Sent To"?: string;
  readonly Driver: string;
  readonly Assets: string;
  readonly "Reference Comment"?: string;
};

const fromELD = (eld: ELD.ELD): Download => ({
  "Report Start Date": Time.DateTime.toDateOnlyMMMDYYYY(eld.interval.start),
  "Report Stop Date": reportStopDate(eld),
  "Submission ID": eld.id,
  "Submission Date": Time.DateTime.toDateOnlyMMMDYYYY(eld.submission),
  "Submitted By": eld.submission.submitter.name,
  "Submission Status": ELD.Submission.Status.toString(eld.submission.status),
  "Sent To": eld.sentTo ?? NotApplicable.text(),
  Driver: Driver.toString(eld.driver),
  Assets: eld.assets.map((asset) => Asset.name(asset)).join(","),
  "Reference Comment": eld.reference,
});

const reportStopDate = (eld: ELD.ELD) =>
  Time.Interval.when(eld, {
    started: () => NotApplicable.text(),
    stopped: ({ interval: { stop } }) => Time.DateTime.toDateOnlyMMMDYYYY(stop),
  });

export const Component = () =>
  // What is on screen, not everything fetched: with the search box working, an
  // export that ignored it would quietly disagree with the table above it.
  pipe(ELD.useAllVisible(), ReadonlyArray.map(fromELD), ([first, ...rest]) =>
    !first ? (
      <Download.Component disabled />
    ) : (
      <CSVLink
        target="_blank"
        headers={Object.keys(first)}
        data={[first, ...rest]}
        filename={`eld-export-${Time.DateTime.toISOWithoutTime(
          Time.DateTime.now()
        )}.csv`}
      >
        <Download.Component />
      </CSVLink>
    )
  );

namespace Download {
  export const Component = (props: { readonly disabled?: boolean }) => (
    <Button.Component
      action
      title="Download"
      icon="$icon_download_line"
      {...props}
    />
  );
}
